import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc, getDoc, setDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../services/firebase';

import {
  Package, ShieldCheck, ShieldAlert, Heart,
  Star, AlertTriangle,
  Carrot, Droplets, Flame, Fish, Cake, Archive,
  Snowflake, Cookie, Coffee, HeartPulse, User,
} from 'lucide-react';
import { useStores } from '../../hooks/useStores';
import AddToListDropdown from './AddToListDropdown';

// ─────────────────────────────────────────────────────────────
// Category config — mirrors iOS ProductCategory enum exactly
// ─────────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  produce:   { icon: Carrot,     gradient: 'from-[#E8F4F1] to-[#C9E8E0]', iconColor: 'text-primary'         },
  dairy:     { icon: Droplets,   gradient: 'from-[#FEF3E2] to-[#FDEBD0]', iconColor: 'text-accent'          },
  meat:      { icon: Flame,      gradient: 'from-[#F0F4FF] to-[#E0E8FF]', iconColor: 'text-[#5B7BCC]'       },
  seafood:   { icon: Fish,       gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary'         },
  bakery:    { icon: Cake,       gradient: 'from-[#FEF3E2] to-[#FDEBD0]', iconColor: 'text-accent'          },
  pantry:    { icon: Archive,    gradient: 'from-[#FEF3E2] to-[#FDEBD0]', iconColor: 'text-accent'          },
  frozen:    { icon: Snowflake,  gradient: 'from-[#F0F4FF] to-[#E0E8FF]', iconColor: 'text-[#5B7BCC]'       },
  snacks:    { icon: Cookie,     gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary'         },
  beverages: { icon: Coffee,     gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary'         },
  health:    { icon: HeartPulse, gradient: 'from-[#E8F4F1] to-[#C9E8E0]', iconColor: 'text-primary'         },
  personal:  { icon: User,       gradient: 'from-[#E8F4F1] to-[#D4EDE8]', iconColor: 'text-primary'         },
};

const DEFAULT_CATEGORY = {
  icon: Package,
  gradient: 'from-[#E8F4F1] to-[#D4EDE8]',
  iconColor: 'text-primary',
};

const getCategoryConfig = (category) =>
  CATEGORY_CONFIG[(category || '').toLowerCase()] || DEFAULT_CATEGORY;

// ─────────────────────────────────────────────────────────────
// Image resolution — mirrors iOS primaryImageURL computed prop
// Checks images[] array first (isPrimary → first), then imageURL
// ─────────────────────────────────────────────────────────────
const getPrimaryImageURL = (product) => {
  const images = product.images;
  if (Array.isArray(images) && images.length > 0) {
    const primary = images.find(img => img.isPrimary);
    const chosen = primary || images[0];
    return chosen?.urls?.medium || chosen?.urls?.full || chosen?.urls?.thumb || null;
  }
  return product.imageURL || product.imageUrl || product.image || null;
};

// ─────────────────────────────────────────────────────────────
// Allergen count — mirrors iOS allergenWarnings computed prop
// ─────────────────────────────────────────────────────────────
const getAllergenCount = (product) => {
  const fromProduct = Array.isArray(product.allergens) ? product.allergens : [];
  const fromIngredients = Array.isArray(product.ingredients)
    ? product.ingredients.flatMap(i => Array.isArray(i.allergens) ? i.allergens : [])
    : [];
  return new Set([...fromProduct, ...fromIngredients]).size;
};

// ─────────────────────────────────────────────────────────────
// Favorites — mirrors iOS userFavoriteProducts collection
// Doc ID: {userId}_{productId}
// ─────────────────────────────────────────────────────────────
const favDocRef = (userId, productId) =>
  doc(db, 'userFavoriteProducts', `${userId}_${productId}`);

const useFavorite = (productId) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !productId) return;
    getDoc(favDocRef(user.uid, productId))
      .then(snap => setIsFavorited(snap.exists()))
      .catch(() => {});
  }, [productId]);

  const toggle = async (e) => {
    e.stopPropagation();
    const user = auth.currentUser;
    if (!user) { alert('Please sign in to save products'); return; }
    setSaving(true);
    try {
      const ref = favDocRef(user.uid, productId);
      if (isFavorited) {
        await deleteDoc(ref);
        setIsFavorited(false);
      } else {
        await setDoc(ref, {
          userId: user.uid,
          productId,
          addedAt: serverTimestamp(),
        });
        setIsFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setSaving(false);
    }
  };

  return { isFavorited, saving, toggle };
};

// ─────────────────────────────────────────────────────────────
// StarRatingRow — read-only, mirrors iOS StarRatingRow
// ─────────────────────────────────────────────────────────────
const StarRatingRow = ({ rating, reviewCount, compact = false }) => {
  const filled = Math.round(rating);
  return (
    <div className="flex items-center gap-xs">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={compact ? 9 : 11}
          className={i <= filled ? 'fill-[#F5A623] text-[#F5A623]' : 'text-text-tertiary/30'}
        />
      ))}
      <span className={`font-semibold text-text-secondary ${compact ? 'text-[10px]' : 'text-caption'}`}>
        {rating.toFixed(1)}
      </span>
      {!compact && reviewCount > 0 && (
        <span className="text-[10px] text-text-tertiary">({reviewCount})</span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// AllergenBadge — mirrors iOS AllergenBadge
// ─────────────────────────────────────────────────────────────
const AllergenBadge = ({ count }) => (
  <div className="flex items-center gap-xs px-sm py-xs bg-orange-500 rounded-full">
    <AlertTriangle size={9} className="text-white" strokeWidth={2.5} />
    <span className="text-[9px] font-bold text-white leading-none">{count}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────
// ProductCard
// ─────────────────────────────────────────────────────────────
const ProductCard = ({ product, onClick, compatibilityResult = null, viewMode = 'grid' }) => {
  const navigate = useNavigate();
  const { stores } = useStores();
  const { isFavorited, saving, toggle: handleToggleSave } = useFavorite(product.id);

  const imageUrl = getPrimaryImageURL(product);
  const allergenCount = getAllergenCount(product);
  const catConfig = getCategoryConfig(product.category);
  const CategoryIcon = catConfig.icon;

  const storeIds = product.storeReports ? Object.keys(product.storeReports) : [];
  const productStores = storeIds.map(id => stores?.find(s => s.id === id)).filter(Boolean);

  const handleBrandClick = (e) => {
    e.stopPropagation();
    if (product.businessId) navigate(`/brands/${product.businessId}`);
  };

  // ── LIST VIEW ──────────────────────────────────────────────
  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-md px-md py-sm bg-background border border-text-tertiary/10 rounded-large hover:border-primary/30 hover:shadow-sm transition-all group">

        {/* Thumbnail */}
        <div
          onClick={onClick}
          className={`w-12 h-12 flex-shrink-0 bg-gradient-to-br ${catConfig.gradient} rounded-medium overflow-hidden flex items-center justify-center cursor-pointer relative`}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform"
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <CategoryIcon size={20} className={catConfig.iconColor} strokeWidth={1.5} />
          )}
          {allergenCount > 0 && (
            <div className="absolute -top-1 -right-1">
              <AllergenBadge count={allergenCount} />
            </div>
          )}
        </div>

        {/* Name + brand */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-xs mb-xs">
            {product.brand && (
              product.businessId ? (
                <button
                  onClick={handleBrandClick}
                  className="text-footnote font-semibold text-primary hover:underline transition-all uppercase tracking-wide"
                >
                  {product.brand}
                </button>
              ) : (
                <span className="text-footnote font-semibold text-primary uppercase tracking-wide">
                  {product.brand}
                </span>
              )
            )}
            {product.isVerified && (
              <ShieldCheck size={11} className="text-success flex-shrink-0" />
            )}
          </div>
          <p
            onClick={onClick}
            className="text-callout font-semibold text-text-primary truncate cursor-pointer"
          >
            {product.name}
          </p>
          {product.rating > 0 && (
            <div className="mt-xs">
              <StarRatingRow rating={product.rating} reviewCount={product.reviewCount || 0} compact />
            </div>
          )}
        </div>

        {/* Dietary tags — up to 2 */}
        <div className="hidden md:flex items-center gap-xs flex-shrink-0">
          {(product.dietaryTags || []).slice(0, 2).map((tag, i) => (
            <span key={i} className="px-sm py-xs bg-accent/10 text-accent text-caption rounded-small">
              {tag}
            </span>
          ))}
        </div>

        {/* Store — first only */}
        {productStores.length > 0 && (
          <span className="hidden lg:block text-caption text-text-tertiary flex-shrink-0 truncate max-w-[100px]">
            {productStores[0].name}
          </span>
        )}

        {/* Compatibility badge */}
        {compatibilityResult && (
          <div className="flex-shrink-0">
            {compatibilityResult.overallSafe ? (
              <div className="w-6 h-6 rounded-full bg-success flex items-center justify-center">
                <ShieldCheck size={13} className="text-white" />
              </div>
            ) : (
              <div className="w-6 h-6 rounded-full bg-error flex items-center justify-center">
                <ShieldAlert size={13} className="text-white" />
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-xs flex-shrink-0 min-w-[100px]" onClick={e => e.stopPropagation()}>
          <button
            onClick={handleToggleSave}
            disabled={saving}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all border ${
              isFavorited
                ? 'bg-error border-error text-white'
                : 'border-text-tertiary/20 text-text-tertiary hover:text-error hover:border-error'
            } disabled:opacity-50`}
          >
            <Heart size={13} className={isFavorited ? 'fill-current' : ''} />
          </button>
          <AddToListDropdown product={product} onSuccess={() => {}} />
        </div>
      </div>
    );
  }

  // ── GRID VIEW ──────────────────────────────────────────────
  return (
    <div className="group bg-white rounded-2xl overflow-hidden flex flex-col border border-text-tertiary/10 hover:border-primary/25 hover:shadow-2xl transition-all duration-200 shadow-md">

      {/* ── Image area ── */}
      <div
        onClick={onClick}
        className={`relative bg-gradient-to-br ${catConfig.gradient} overflow-hidden cursor-pointer`}
        style={{ height: '180px' }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 p-2"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CategoryIcon size={48} className={`${catConfig.iconColor} opacity-30`} strokeWidth={1} />
          </div>
        )}

        {/* Favorite — top left */}
        <button
          onClick={handleToggleSave}
          disabled={saving}
          className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-md hover:scale-110 disabled:opacity-50 ${
            isFavorited ? 'bg-error text-white' : 'bg-white/90 text-text-tertiary hover:text-error'
          }`}
        >
          <Heart size={15} className={isFavorited ? 'fill-current' : ''} />
        </button>

        {/* Compatibility badge — top right */}
        {compatibilityResult && (
          <div className="absolute top-3 right-3">
            {compatibilityResult.overallSafe ? (
              <div className="w-8 h-8 rounded-full bg-success flex items-center justify-center shadow-md">
                <ShieldCheck size={16} className="text-white" />
              </div>
            ) : compatibilityResult.safeCount > 0 ? (
              <div className="w-8 h-8 rounded-full bg-warning flex items-center justify-center shadow-md">
                <ShieldAlert size={16} className="text-white" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-error flex items-center justify-center shadow-md">
                <ShieldAlert size={16} className="text-white" />
              </div>
            )}
          </div>
        )}

        {/* Allergen badge — bottom left */}
        {allergenCount > 0 && (
          <div className="absolute bottom-3 left-3">
            <AllergenBadge count={allergenCount} />
          </div>
        )}

        {/* Dietary tag — bottom right */}
        {(product.dietaryTags || []).length > 0 && (
          <div className="absolute bottom-3 right-3">
            <span className={`text-[10px] font-semibold px-2 py-1 bg-white/95 rounded-full ${catConfig.iconColor} shadow-sm`}>
              {product.dietaryTags[0]}
            </span>
          </div>
        )}
      </div>

      {/* ── Card body ── */}
      <div onClick={onClick} className="cursor-pointer flex-1" style={{ padding: '16px 16px 12px' }}>

        {/* Brand + verified */}
        <div className="flex items-center gap-1.5" style={{ marginBottom: '6px' }}>
          {product.brand && (
            product.businessId ? (
              <button
                onClick={handleBrandClick}
                className="text-[11px] font-bold text-primary hover:underline uppercase tracking-wider leading-none"
              >
                {product.brand}
              </button>
            ) : (
              <span className="text-[11px] font-bold text-primary uppercase tracking-wider leading-none">
                {product.brand}
              </span>
            )
          )}
          {product.isVerified && (
            <ShieldCheck size={12} className="text-success flex-shrink-0" />
          )}
        </div>

        {/* Product name */}
        <h3 className="text-sm font-semibold text-text-primary leading-snug line-clamp-2" style={{ marginBottom: '10px', minHeight: '2.5rem' }}>
          {product.name}
        </h3>

        {/* Star rating */}
        {product.rating > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <StarRatingRow
              rating={product.rating}
              reviewCount={product.reviewCount || 0}
              compact
            />
          </div>
        )}

        {/* Allergen warning */}
        {allergenCount > 0 && (
          <div className="flex items-center gap-1" style={{ marginBottom: '4px' }}>
            <AlertTriangle size={10} className="text-orange-500 flex-shrink-0" />
            <span className="text-[11px] font-medium text-orange-500">
              {allergenCount} allergen{allergenCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Compatibility detail */}
      {compatibilityResult && (
        <div style={{ margin: '0 16px 8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          {compatibilityResult.overallSafe ? (
            <div className="flex items-center gap-1.5" style={{ marginBottom: '4px' }}>
              <ShieldCheck size={12} className="text-success" />
              <p className="text-xs font-semibold text-success">Safe for everyone</p>
            </div>
          ) : compatibilityResult.safeCount > 0 ? (
            <div className="flex items-center gap-1.5" style={{ marginBottom: '4px' }}>
              <ShieldAlert size={12} className="text-warning" />
              <p className="text-xs font-semibold text-warning">
                Safe for {compatibilityResult.safeCount} of {compatibilityResult.totalProfiles}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-1.5" style={{ marginBottom: '4px' }}>
              <ShieldAlert size={12} className="text-error" />
              <p className="text-xs font-semibold text-error">Not safe for anyone</p>
            </div>
          )}
          {compatibilityResult.profileStatuses && (
            <div className="flex flex-wrap gap-1">
              {compatibilityResult.profileStatuses.map((status) => (
                <span
                  key={status.profileId}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 ${
                    status.isCompatible ? 'bg-success text-white' : 'bg-error text-white'
                  }`}
                >
                  {status.isCompatible ? <ShieldCheck size={9} /> : <ShieldAlert size={9} />}
                  {status.profileName}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add to list */}
      <div
        style={{ padding: '12px 16px 16px', borderTop: '1px solid rgba(0,0,0,0.06)' }}
        onClick={e => e.stopPropagation()}
      >
        <AddToListDropdown product={product} onSuccess={() => {}} />
      </div>
    </div>
  );
};

export default ProductCard;